import { useEffect, useState } from "react";
import api, { formatTL, formatNumber } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import KPICard from "@/components/app/KPICard";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend, PieChart, Pie, Cell
} from "recharts";
import { TrendingUp, TrendingDown, Wallet, Fuel, Users, Truck, Building2, FileSignature, Gauge, AlertTriangle, ArrowRight, Calendar } from "lucide-react";
import { Link } from "react-router-dom";

const monthLabel = (m) => {
  if (!m || m === "unknown") return "-";
  const [y, mo] = m.split("-");
  const names = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y?.slice(2)}`;
};

const SHORT_MONTHS = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
const YEARS_TO_SHOW = [2024, 2025, 2026];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [driverPerf, setDriverPerf] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [vehicleAlerts, setVehicleAlerts] = useState(null);
  const [threshold, setThreshold] = useState(50000);
  const [yearlyData, setYearlyData] = useState({}); // { 2024: {...}, 2025: {...}, 2026: {...} }

  useEffect(() => {
    (async () => {
      const [s, d, a, va] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/driver-performance"),
        api.get(`/dashboard/cari-alerts?threshold=${threshold}`),
        api.get("/dashboard/vehicle-alerts").catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setDriverPerf(d.data);
      setAlerts(a.data);
      setVehicleAlerts(va.data);
      // Fetch yearly consolidated for each year in parallel
      const yearlyResults = await Promise.all(
        YEARS_TO_SHOW.map(y => api.get(`/reports/yearly-consolidated?year=${y}`).then(r => [y, r.data]).catch(() => [y, null]))
      );
      const yObj = {};
      yearlyResults.forEach(([y, d]) => { if (d) yObj[y] = d; });
      setYearlyData(yObj);
    })().catch(() => {});
  }, [threshold]);

  if (!stats) return <div className="p-8 text-neutral-500">Yükleniyor...</div>;

  const monthly = (stats.monthly || []).map(m => ({ ...m, label: monthLabel(m.month) }));
  const expenseBreakdown = [
    { name: "Yakıt", value: stats.financials.total_fuel, color: "#DC2626" },
    { name: "Maaşlar", value: stats.financials.total_salaries, color: "#0020C2" },
    { name: "Tedarikçi", value: stats.financials.total_supplier, color: "#F59E0B" },
    { name: "Faturalar", value: stats.financials.total_invoices, color: "#0A0A0A" },
    { name: "Ekstra", value: stats.financials.total_extra_expenses, color: "#7C3AED" },
  ].filter(x => x.value > 0);

  const isProfitPositive = stats.financials.net_profit >= 0;

  return (
    <>
      <PageHeader
        title="Kontrol Paneli"
        subtitle="Şirketinizin finansal durumu, filo verimliliği ve pazarlama aktiviteleri bir arada."
        testid="dashboard-header"
      />

      {/* Yıllık Bazlı Özet */}
      {Object.keys(yearlyData).length > 0 && (
        <div className="mb-6" data-testid="yearly-breakdown">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-neutral-600 font-semibold">
              <Calendar className="w-3.5 h-3.5" />
              Yıllık Bazlı Kar-Zarar
            </div>
            <Link to="/yearly-consolidated" className="text-[10px] uppercase tracking-widest text-[#0020C2] hover:underline font-semibold">
              Detaylı Görüntüle →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {YEARS_TO_SHOW.map(y => {
              const yd = yearlyData[y];
              if (!yd) return null;
              const g = yd.grand || {};
              const monthly = yd.monthly || {};
              const maxVal = Math.max(
                ...(monthly.sales || [0]),
                ...(monthly.supplier_inv || [0]),
                ...(monthly.fuel || [0]),
                ...(monthly.salary || [0]),
                1
              );
              const income = g.sales_total || 0; // araç kazançları ayrı bölümde gösterilir
              const expenses = (g.supplier_invoice_total || 0) + (g.fuel_total || 0) + (g.salary_total || 0) + (g.extra_expense_total || 0);
              const net = income - expenses;
              return (
                <div key={y} className="bg-white border border-[#E5E5E5] rounded-md p-4">
                  <div className="flex items-baseline justify-between mb-3 pb-2 border-b border-[#F5F5F5]">
                    <div className="font-heading font-black text-2xl">{y}</div>
                    <div className={`text-[11px] uppercase tracking-widest font-semibold ${net >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
                      Net: {formatTL(net)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500">Kesilen Fatura</div>
                      <div className="num text-sm font-bold text-[#0020C2]">{formatTL(g.sales_total || 0)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500">Tahsilat</div>
                      <div className="num text-sm font-bold text-[#16A34A]">{formatTL(g.incoming_total || 0)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500">Tedarikçi Gid.</div>
                      <div className="num text-sm font-bold text-[#DC2626]">{formatTL(g.supplier_invoice_total || 0)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500">Yakıt Gideri</div>
                      <div className="num text-sm font-bold text-[#F59E0B]">{formatTL(g.fuel_total || 0)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500">Maaş Gideri</div>
                      <div className="num text-sm font-bold text-[#F59E0B]">{formatTL(g.salary_total || 0)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500">Ekstra Gider</div>
                      <div className="num text-sm font-bold text-[#7C3AED]">{formatTL(g.extra_expense_total || 0)}</div>
                    </div>
                  </div>
                  {/* Aylık mini bar chart */}
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Aylık Kırılım (Gelir vs Gider)</div>
                    <div className="flex items-end gap-0.5 h-16">
                      {SHORT_MONTHS.map((mn, i) => {
                        const sales = (monthly.sales || [])[i] || 0;
                        const expM = ((monthly.supplier_inv || [])[i] || 0) + ((monthly.fuel || [])[i] || 0) + ((monthly.salary || [])[i] || 0) + ((monthly.extra_expense || [])[i] || 0);
                        const incM = sales; // araç kazançları hariç — ayrı bölümde
                        const incH = (incM / maxVal) * 100;
                        const expH = (expM / maxVal) * 100;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${mn}: Gelir ${formatTL(incM)}, Gider ${formatTL(expM)}`}>
                            <div className="w-full flex items-end gap-[1px] h-14">
                              <div className="flex-1 bg-[#16A34A] rounded-t-sm" style={{height: `${incH}%`, minHeight: incM > 0 ? '2px' : '0'}} />
                              <div className="flex-1 bg-[#DC2626] rounded-t-sm" style={{height: `${expH}%`, minHeight: expM > 0 ? '2px' : '0'}} />
                            </div>
                            <div className="text-[8px] text-neutral-400 num">{mn}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[9px] text-neutral-500">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#16A34A]"></span> Gelir</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#DC2626]"></span> Gider</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Araç Kazançları — AYRI BÖLÜM (aylık gelir kırılımına dahil edilmez) */}
      {Object.keys(yearlyData).length > 0 && (
        <div className="mb-6" data-testid="vehicle-earnings-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-neutral-600 font-semibold">
              <Truck className="w-3.5 h-3.5" />
              Araç Kazançları (Ayrı Takip)
            </div>
            <Link to="/vehicle-earnings" className="text-[10px] uppercase tracking-widest text-[#0020C2] hover:underline font-semibold">
              Detaylı Görüntüle →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {YEARS_TO_SHOW.map(y => {
              const yd = yearlyData[y];
              if (!yd) return null;
              const total = yd.grand?.vehicle_earnings_total || 0;
              const earnMonthly = yd.monthly?.earnings || [];
              const maxE = Math.max(...earnMonthly, 1);
              return (
                <div key={y} className="bg-white border border-[#E5E5E5] border-l-4 border-l-[#0020C2] rounded-md p-4" data-testid={`vehicle-earnings-${y}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="font-heading font-black text-xl">{y}</div>
                    <div className="num font-bold text-base text-[#0020C2]">{formatTL(total)}</div>
                  </div>
                  <div className="flex items-end gap-0.5 h-12">
                    {SHORT_MONTHS.map((mn, i) => {
                      const v = earnMonthly[i] || 0;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${mn}: ${formatTL(v)}`}>
                          <div className="w-full flex items-end h-10">
                            <div className="flex-1 bg-[#0020C2] rounded-t-sm" style={{ height: `${(v / maxE) * 100}%`, minHeight: v > 0 ? "2px" : "0" }} />
                          </div>
                          <div className="text-[8px] text-neutral-400 num">{mn}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Araç Bakım & Muayene Uyarıları */}
      {vehicleAlerts && vehicleAlerts.count > 0 && (
        <div className="bg-white border-l-4 border-l-[#DC2626] border border-[#E5E5E5] rounded-md p-4 mb-6" data-testid="alert-vehicles">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 bg-[#FEE2E2] flex items-center justify-center shrink-0"><Truck className="w-5 h-5 text-[#DC2626]" /></div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Araç Bakım & Muayene Uyarısı</div>
                <div className="font-heading font-bold text-base">{vehicleAlerts.count} Uyarı{vehicleAlerts.overdue_count > 0 ? ` · ${vehicleAlerts.overdue_count} Gecikmiş` : ""}</div>
              </div>
            </div>
          </div>
          <div className="space-y-1 max-h-[200px] overflow-y-auto">
            {vehicleAlerts.alerts.slice(0, 10).map((a, i) => (
              <div key={`${a.vehicle_id}-${a.type}-${i}`} className="flex items-center justify-between text-xs px-2 py-1.5 hover:bg-[#FAFAFA] border-b border-[#F5F5F5]" data-testid={`vehicle-alert-row-${i}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <span className="num font-semibold shrink-0">{a.plate}</span>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 shrink-0 ${a.overdue ? "bg-[#DC2626] text-white" : "bg-[#FEF3C7] text-[#92400E]"}`}>
                    {{ inspection: "Muayene", insurance: "Sigorta", kasko: "Kasko", maintenance_km: "Bakım (KM)" }[a.type] || "Bakım"}
                  </span>
                  <span className="truncate text-neutral-500">{a.model}</span>
                </span>
                <span className={`num font-semibold ml-2 shrink-0 ${a.overdue ? "text-[#DC2626]" : "text-[#92400E]"}`}>
                  {a.type === "maintenance_km"
                    ? (a.overdue ? `${formatNumber(a.current_km)} km · hedef aşıldı` : `${formatNumber(a.km_left)} km kaldı`)
                    : (a.overdue ? `${a.due_date.split("-").reverse().join(".")} · ${Math.abs(a.days_left)} gün geçti` : `${a.due_date.split("-").reverse().join(".")} · ${a.days_left} gün kaldı`)}
                </span>
              </div>
            ))}
          </div>
          <Link to="/vehicles" data-testid="alert-vehicle-link" className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-[#0020C2] hover:text-[#0018A0]">
            Araçlara Git <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}

      {/* Cari Alerts */}
      {alerts && (alerts.overdue_suppliers.length > 0 || alerts.overdue_customers.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {alerts.overdue_suppliers.length > 0 && (
            <div className="bg-white border-l-4 border-l-[#DC2626] border border-[#E5E5E5] rounded-md p-4" data-testid="alert-suppliers">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-9 h-9 bg-[#FEE2E2] flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5 text-[#DC2626]" /></div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Vadeli Borç Uyarısı</div>
                    <div className="font-heading font-bold text-base">{alerts.overdue_suppliers.length} Tedarikçi Borçlu</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Borç</div>
                  <div className="num font-bold text-lg text-[#DC2626]">{formatTL(alerts.supplier_debt_total)}</div>
                </div>
              </div>
              <div className="space-y-1 max-h-[180px] overflow-y-auto">
                {alerts.overdue_suppliers.slice(0, 6).map(s => (
                  <div key={s.id} className="flex items-center justify-between text-xs px-2 py-1.5 hover:bg-[#FAFAFA] border-b border-[#F5F5F5]">
                    <span className="truncate font-medium">{s.name}</span>
                    <span className="num font-semibold text-[#DC2626] ml-2">{formatTL(s.balance)}</span>
                  </div>
                ))}
              </div>
              <Link to="/supplier-accounts" data-testid="alert-supplier-link" className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-[#0020C2] hover:text-[#0018A0]">
                Tedarikçi Cari&apos;ye Git <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          )}
          {alerts.overdue_customers.length > 0 && (
            <div className="bg-white border-l-4 border-l-[#F59E0B] border border-[#E5E5E5] rounded-md p-4" data-testid="alert-customers">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-9 h-9 bg-[#FEF3C7] flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5 text-[#F59E0B]" /></div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Tahsilat Uyarısı</div>
                    <div className="font-heading font-bold text-base">{alerts.overdue_customers.length} Müşteri Alacaklı</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Alacak</div>
                  <div className="num font-bold text-lg text-[#F59E0B]">{formatTL(alerts.customer_receivable_total)}</div>
                </div>
              </div>
              <div className="space-y-1 max-h-[180px] overflow-y-auto">
                {alerts.overdue_customers.slice(0, 6).map(c => (
                  <div key={c.id} className="flex items-center justify-between text-xs px-2 py-1.5 hover:bg-[#FAFAFA] border-b border-[#F5F5F5]">
                    <span className="truncate font-medium">{c.name}</span>
                    <span className="num font-semibold text-[#F59E0B] ml-2">{formatTL(c.balance)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <Link to="/customer-accounts" data-testid="alert-customer-link" className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-[#0020C2] hover:text-[#0018A0]">
                  Müşteri Cari&apos;ye Git <ArrowRight className="w-3 h-3" />
                </Link>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="text-neutral-500 uppercase tracking-widest font-semibold">Eşik:</span>
                  <select value={threshold} onChange={e => setThreshold(Number(e.target.value))} className="px-2 py-1 border border-[#E5E5E5] num" data-testid="alert-threshold">
                    <option value={10000}>10.000 ₺</option>
                    <option value={50000}>50.000 ₺</option>
                    <option value={100000}>100.000 ₺</option>
                    <option value={500000}>500.000 ₺</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard testid="kpi-income" label="Toplam Gelir" value={formatTL(stats.financials.total_income)}
                 sub="Kesilen fatura · Araç kazancı · Tahsilat" accent="success" icon={TrendingUp} />
        <KPICard testid="kpi-expenses" label="Toplam Gider" value={formatTL(stats.financials.total_expenses)}
                 sub="Yakıt · Maaş · Fatura · Tedarikçi" accent="error" icon={TrendingDown} />
        <KPICard testid="kpi-profit" label="Net Kar / Zarar" value={formatTL(stats.financials.net_profit)}
                 sub={isProfitPositive ? "Kar durumu" : "Zarar durumu"} accent={isProfitPositive ? "success" : "error"} icon={Wallet} />
        <KPICard testid="kpi-salary-liability" label="Aylık Maaş Yükü" value={formatTL(stats.financials.monthly_salary_liability)}
                 sub="Şoför + Personel taahhüdü" accent="info" icon={Users} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <KPICard testid="kpi-drivers" label="Şoför" value={formatNumber(stats.counts.drivers)} sub="aktif kayıt" icon={Users} />
        <KPICard testid="kpi-vehicles" label="Araç" value={formatNumber(stats.counts.vehicles)} sub="filoda" icon={Truck} />
        <KPICard testid="kpi-avg-fuel" label="Ort. Tüketim" value={`${formatNumber(stats.fleet.avg_consumption, 2)} L/100km`} sub={`${formatNumber(stats.fleet.total_km)} km`} accent="warning" icon={Gauge} />
        <KPICard testid="kpi-companies" label="Firma" value={formatNumber(stats.counts.companies)} sub="pazarlama listesi" icon={Building2} />
        <KPICard testid="kpi-tenders" label="Açık İhale" value={formatNumber(stats.counts.open_tenders)} sub="takipte" accent="info" icon={FileSignature} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 bg-white border border-[#E5E5E5] p-5 rounded-md" data-testid="chart-monthly">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Aylık Gelir & Gider</div>
              <div className="text-lg font-bold font-heading mt-1">Nakit Akış Grafiği</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <AreaChart data={monthly}>
                <defs>
                  <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16A34A" stopOpacity={0.35}/>
                    <stop offset="95%" stopColor="#16A34A" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#DC2626" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#DC2626" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#525252" }} />
                <YAxis tick={{ fontSize: 11, fill: "#525252" }} tickFormatter={(v)=> new Intl.NumberFormat("tr-TR",{notation:"compact"}).format(v)} />
                <Tooltip formatter={(v) => formatTL(v)} contentStyle={{ fontSize: 12, border: "1px solid #E5E5E5" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="income" name="Gelir" stroke="#16A34A" fill="url(#gInc)" strokeWidth={2} />
                <Area type="monotone" dataKey="expense" name="Gider" stroke="#DC2626" fill="url(#gExp)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-[#E5E5E5] p-5 rounded-md" data-testid="chart-expense-breakdown">
          <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Gider Dağılımı</div>
          <div className="text-lg font-bold font-heading mt-1 mb-4">Kategoriler</div>
          {expenseBreakdown.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-neutral-500">Henüz gider kaydı yok</div>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={expenseBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {expenseBreakdown.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatTL(v)} contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Profit and Driver Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E5E5E5] p-5 rounded-md" data-testid="chart-profit">
          <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Aylık Net Kar</div>
          <div className="text-lg font-bold font-heading mt-1 mb-4">Karlılık Analizi</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#525252" }} />
                <YAxis tick={{ fontSize: 11, fill: "#525252" }} tickFormatter={(v)=> new Intl.NumberFormat("tr-TR",{notation:"compact"}).format(v)} />
                <Tooltip formatter={(v) => formatTL(v)} contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="profit" name="Kar/Zarar" radius={[4,4,0,0]}>
                  {monthly.map((m, i) => <Cell key={i} fill={m.profit >= 0 ? "#16A34A" : "#DC2626"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-[#E5E5E5] p-5 rounded-md" data-testid="driver-performance-table">
          <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Şoför Performansı</div>
          <div className="text-lg font-bold font-heading mt-1 mb-4">Km / Yakıt Analizi</div>
          {driverPerf.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-sm text-neutral-500">Henüz şoför veya km kaydı yok</div>
          ) : (
            <div className="overflow-x-auto data-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-[#E5E5E5]">
                    <th className="py-2 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Şoför</th>
                    <th className="py-2 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold text-right">Km</th>
                    <th className="py-2 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold text-right">Ort. L/100km</th>
                    <th className="py-2 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold text-right">Sefer</th>
                  </tr>
                </thead>
                <tbody>
                  {driverPerf.slice(0, 8).map((d) => (
                    <tr key={d.driver_id} className="border-b border-[#F5F5F5]">
                      <td className="py-2.5 font-medium">{d.name}</td>
                      <td className="py-2.5 text-right num">{formatNumber(d.total_km)}</td>
                      <td className="py-2.5 text-right num">{formatNumber(d.avg_consumption, 2)}</td>
                      <td className="py-2.5 text-right num text-neutral-600">{d.trip_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
